import { bytesToHex, hexToBytes, toArrayBuffer } from "./bytes";
import type { EncryptedPayload } from "./types";

const ALGORITHM = "AES-GCM";
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const TAG_LENGTH = 16;

async function importKey(keyHex: string): Promise<CryptoKey> {
  if (keyHex.length !== 64 || !/^[0-9a-fA-F]{64}$/.test(keyHex)) {
    throw new Error("Transport key must be 64 hex characters.");
  }
  return crypto.subtle.importKey(
    "raw",
    toArrayBuffer(hexToBytes(keyHex)),
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptWithKeyHex(
  text: string,
  keyHex: string,
): Promise<EncryptedPayload> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv, tagLength: TAG_LENGTH * 8 },
    await importKey(keyHex),
    new TextEncoder().encode(text),
  );
  const bytes = new Uint8Array(encryptedBuffer);
  const encryptedData = bytes.slice(0, -TAG_LENGTH);
  const tag = bytes.slice(-TAG_LENGTH);
  return {
    encrypted: bytesToHex(encryptedData),
    iv: bytesToHex(iv),
    tag: bytesToHex(tag),
  };
}

export async function decryptWithKeyHex(
  payload: EncryptedPayload,
  keyHex: string,
): Promise<string> {
  const encrypted = hexToBytes(payload.encrypted);
  const tag = hexToBytes(payload.tag);
  const combined = new Uint8Array(encrypted.length + tag.length);
  combined.set(encrypted);
  combined.set(tag, encrypted.length);
  const decrypted = await crypto.subtle.decrypt(
    {
      name: ALGORITHM,
      iv: toArrayBuffer(hexToBytes(payload.iv)),
      tagLength: TAG_LENGTH * 8,
    },
    await importKey(keyHex),
    toArrayBuffer(combined),
  );
  return new TextDecoder().decode(decrypted);
}
